import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { Icon } from "../../components/Icon/Icon";
import {
  streamAgentReply,
  type ChatMessage,
  type StreamHandlers,
  makeConversation,
} from "../../api/aiAgent";

const useStyles = createUseStyles({
  root: { display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" },
  sidebarOuter: { overflow: "hidden", height: "100%" },
  header: { display: "flex", alignItems: "center", gap: "12px", padding: "20px 24px 16px", borderBottom: `1px solid ${colors.borderLight}`, flexShrink: 0, "@media (max-width:899.95px)": { padding: "14px 16px 12px" } },
  headerIcon: { width: "40px", height: "40px", borderRadius: "12px", background: `linear-gradient(135deg, ${colors.primary}, ${colors.electricBlue || colors.primary})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  headerTitle: { fontSize: "18px", fontWeight: 600, color: colors.textPrimary, margin: 0 },
  headerSub: { fontSize: "13px", color: colors.textSecondary || "#999", margin: 0 },
  messagesArea: { flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-thumb": { background: colors.borderLight, borderRadius: "4px" }, "@media (max-width:899.95px)": { padding: "16px" } },
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "16px", padding: "40px" },
  emptyIcon: { width: "64px", height: "64px", borderRadius: "50%", background: `linear-gradient(135deg, ${colors.primary}22, ${colors.primary}11)`, display: "flex", alignItems: "center", justifyContent: "center" },
  emptyTitle: { fontSize: "18px", fontWeight: 600, color: colors.textPrimary, textAlign: "center" as const },
  emptyDesc: { fontSize: "14px", color: colors.textSecondary || "#999", textAlign: "center" as const, maxWidth: "360px", lineHeight: "1.5" },
  suggestionsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px", width: "100%", maxWidth: "500px", "@media (max-width:479.95px)": { gridTemplateColumns: "1fr" } },
  suggestionBtn: { padding: "10px 14px", borderRadius: "10px", border: `1px solid ${colors.borderLight}`, background: colors.surface, color: colors.textPrimary, fontSize: "13px", cursor: "pointer", textAlign: "left" as const, transition: "border-color 0.15s", "&:hover": { borderColor: colors.primary, background: colors.surfaceHover } },
  msgRow: { display: "flex", gap: "10px", maxWidth: "85%", "@media (max-width:899.95px)": { maxWidth: "95%" } },
  msgRowUser: { alignSelf: "flex-end", flexDirection: "row-reverse" as const },
  msgRowAi: { alignSelf: "flex-start" },
  msgBubble: { padding: "12px 16px", borderRadius: "14px", fontSize: "14px", lineHeight: "1.6", whiteSpace: "pre-wrap" as const, wordBreak: "break-word" as const, "& p": { margin: "4px 0" }, "& code": { background: "rgba(0,0,0,0.06)", padding: "2px 6px", borderRadius: "4px", fontSize: "13px" } },
  msgUser: { background: colors.primary, color: "#fff", borderBottomRightRadius: "4px" },
  msgAi: { background: colors.elevatedSurface || colors.surface, color: colors.textPrimary, borderBottomLeftRadius: "4px" },
  avatar: { width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "2px" },
  avatarAi: { background: `linear-gradient(135deg, ${colors.primary}, ${colors.electricBlue || colors.primary})` },
  avatarUser: { background: colors.surface, border: `1px solid ${colors.borderLight}` },
  typingDots: { display: "flex", gap: "4px", padding: "8px 0", "& span": { width: "6px", height: "6px", borderRadius: "50%", background: colors.textSecondary || "#999", animation: "$blink 1.4s infinite both", "&:nth-child(2)": { animationDelay: "0.2s" }, "&:nth-child(3)": { animationDelay: "0.4s" } } },
  "@keyframes blink": { "0%, 80%, 100%": { opacity: 0.3 }, "40%": { opacity: 1 } },
  inputArea: { padding: "12px 24px 20px", borderTop: `1px solid ${colors.borderLight}`, flexShrink: 0, "@media (max-width:899.95px)": { padding: "10px 16px calc(16px + env(safe-area-inset-bottom))" } },
  inputRow: { display: "flex", alignItems: "center", gap: "10px", background: colors.surface, border: `1px solid ${colors.borderLight}`, borderRadius: "14px", padding: "6px 6px 6px 16px", transition: "border-color 0.15s", "&:focus-within": { borderColor: colors.primary } },
  input: { flex: 1, background: "none", border: "none", outline: "none", color: colors.textPrimary, fontSize: "14px", padding: "8px 0", "&::placeholder": { color: colors.textSecondary || "#999" } },
  sendBtn: { width: "36px", height: "36px", borderRadius: "10px", border: "none", background: colors.primary, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, "&:disabled": { opacity: 0.4, cursor: "not-allowed" } },

});

export const Aide: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [conv] = useState(() => makeConversation("Help"));
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const suggestions = [
    t("aide.suggestion1", "How do I add a vehicle?"),
    t("aide.suggestion2", "How to manage my drivers?"),
    t("aide.suggestion3", "Where can I see my documents?"),
    t("aide.suggestion4", "How does billing work?"),
  ];

  const sendMessage = async (text: string) => {
    if (!text.trim() || isStreaming) return;

    const userMsg: ChatMessage = { id: Date.now().toString(), role: "user", content: text.trim(), createdAt: Date.now() };
    const aiId = (Date.now() + 1).toString();
    const aiMsg: ChatMessage = { id: aiId, role: "assistant", content: "", createdAt: Date.now(), streaming: true };

    setMessages((prev) => [...prev, userMsg, aiMsg]);
    setInput("");
    setIsStreaming(true);

    const handlers: StreamHandlers = {
      onToken: (chunk: string) => {
        setMessages((prev) => prev.map((m) => m.id === aiId ? { ...m, content: m.content + chunk } : m));
      },
      onDone: (done: ChatMessage) => {
        setMessages((prev) => prev.map((m) => m.id === aiId ? { ...done, id: aiId, streaming: false } : m));
        setIsStreaming(false);
      },
      onError: () => {
        setMessages((prev) => prev.map((m) => m.id === aiId ? { ...m, content: t("aide.errorMessage", "Sorry, an error occurred. Please try again."), streaming: false, error: true } : m));
        setIsStreaming(false);
      },
    };

    try {
      await streamAgentReply(text.trim(), messages, handlers, conv.id, "guide");
    } catch {
      handlers.onError?.(new Error("failed"));
    }
  };

  return (
    <Sidebar styles={{ sidebarRightRoot: classes.root, sidebarRoot: classes.sidebarOuter }}>
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerIcon}>
            <Icon name="sparkles" width="22px" height="22px" color="#fff" />
          </div>
          <div>
            <p className={classes.headerTitle}>{t("aide.title", "Help Center")}</p>
            <p className={classes.headerSub}>{t("aide.subtitle", "How to use CodeNekt")}</p>
          </div>
        </div>

        <div className={classes.messagesArea}>
          {messages.length === 0 ? (
            <div className={classes.emptyState}>
              <div className={classes.emptyIcon}>
                <Icon name="sparkles" width="32px" height="32px" color={colors.primary} />
              </div>
              <div className={classes.emptyTitle}>{t("aide.emptyTitle", "How can I help you?")}</div>
              <div className={classes.emptyDesc}>{t("aide.emptyDesc", "I can help you navigate the platform, manage your fleet, understand features, and more.")}</div>
              <div className={classes.suggestionsGrid}>
                {suggestions.map((s, i) => (
                  <button key={i} className={classes.suggestionBtn} onClick={() => sendMessage(s)}>{s}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className={`${classes.msgRow} ${msg.role === "user" ? classes.msgRowUser : classes.msgRowAi}`}>
                <div className={`${classes.avatar} ${msg.role === "user" ? classes.avatarUser : classes.avatarAi}`}>
                  <Icon name={msg.role === "user" ? "userCircle" : "sparkles"} width="16px" height="16px" color="#fff" />
                </div>
                <div className={`${classes.msgBubble} ${msg.role === "user" ? classes.msgUser : classes.msgAi}`}>
                  {msg.role === "assistant" && msg.content === "" && msg.streaming ? (
                    <div className={classes.typingDots}><span /><span /><span /></div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <div className={classes.inputArea}>
          <div className={classes.inputRow}>
            <input
              className={classes.input}
              placeholder={t("aide.inputPlaceholder", "Ask a question...")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
              disabled={isStreaming}
            />
            <button className={classes.sendBtn} onClick={() => sendMessage(input)} disabled={!input.trim() || isStreaming}>
              <Icon name="chevronRight" width="18px" height="18px" color="#fff" />
            </button>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};
